<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcValidityPeriod',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    from: {
      type: [String, Date],
      required: true,
    },
    to: {
      type: [String, Date],
      required: false,
    },
    fromTooltip: {
      type: String,
      required: false,
    },
    toTooltip: {
      type: String,
      required: false,
    },
    missingFromMessage: {
      type: String,
      required: false,
    },
    missingToMessage: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const defaultFromTooltip = ref(
    $fn.value.pgettext('ValidityPeriod', 'Valid from')
  );
  const defaultToTooltip = ref(
    $fn.value.pgettext('ValidityPeriod', 'Valid to')
  );
  const defaultMissingFromMessage = ref(
    $fn.value.pgettext('UcValidityPeriod', 'N/A')
  );
  const defaultMissingToMessage = ref(
    $fn.value.pgettext('UcValidityPeriod', 'Indefinite')
  );
  const ux = ref([]);

  const $data = computed(() => ({
    defaultFromTooltip: defaultFromTooltip.value,
    defaultToTooltip: defaultToTooltip.value,
    defaultMissingFromMessage: defaultMissingFromMessage.value,
    defaultMissingToMessage: defaultMissingToMessage.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'defaultFromTooltip' },
      { name: 'defaultToTooltip' },
      { name: 'defaultMissingFromMessage' },
      { name: 'defaultMissingToMessage' },
      { name: 'ux' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      defaultFromTooltip,
      defaultToTooltip,
      defaultMissingFromMessage,
      defaultMissingToMessage,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.from,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('from');
    }
  );
  watch(
    () => props.to,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('to');
    }
  );
  watch(
    () => props.fromTooltip,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fromTooltip');
    }
  );
  watch(
    () => props.toTooltip,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('toTooltip');
    }
  );
  watch(
    () => props.missingFromMessage,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('missingFromMessage');
    }
  );
  watch(
    () => props.missingToMessage,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('missingToMessage');
    }
  );

  const act_7315092382398562_reloadComponentData_ebfjvht8y =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_d3i24k4oa =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ebfjvht8y,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_d3i24k4oa],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_d3i24k4oa,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_d3i24k4oa = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_d3i24k4oa.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox alignItems="center" class="text-nowrap">
      <ItpText :title="fromTooltip || defaultFromTooltip">
        {{
          from ? date(from) : missingFromMessage || defaultMissingFromMessage
        }}
      </ItpText>
      <span v-if="to || missingToMessage || defaultMissingToMessage">
        &nbsp;-&nbsp;
      </span>
      <ItpText
        :title="toTooltip || defaultToTooltip"
        v-if="to || missingToMessage || defaultMissingToMessage"
      >
        {{ to ? date(to) : missingToMessage || defaultMissingToMessage }}
      </ItpText>
    </ItpBox>
  </ItpUsercontrol>
</template>
